import { Component, OnInit, Output, EventEmitter, Input, SimpleChanges, ViewChild, OnChanges } from '@angular/core';
import { MatSnackBar } from '@angular/material/snack-bar';
import { MatPaginator } from '@angular/material/paginator';
import { MatTableDataSource } from '@angular/material/table';
import { SelectionModel } from '@angular/cdk/collections';
import { MatDialogRef } from '@angular/material/dialog';

import { Helper, KeyEventListener, PointSelectionService } from '@usimweb/core/services';
import { Gravity, NozzlePoint } from '@usimweb/core/models';
import { Vec3 } from '@usimweb/ceetron';

@Component({
    selector: 'app-open-mold-dialog',
    templateUrl: './open-mold-dialog.component.html',
    styleUrls: ['./open-mold-dialog.component.scss'],
    standalone: false
})
export class OpenMoldDialogComponent extends KeyEventListener implements OnInit, OnChanges {
  @Input() data = {
    injectionTime: 0,
    modelId: 0,
    gravity: { x: 0, y: 0, z: 0 },
  };

  @Input() saveTrigger: boolean = false;

  @Output() saveCalled: EventEmitter<any> = new EventEmitter<any>();

  @Output() resetTrigger: EventEmitter<any> = new EventEmitter<any>();

  @ViewChild(MatPaginator, { static: true }) paginator!: MatPaginator;

  private pageIndex = 0;
  private pageSize = 0;
  private modelId: number = 0;
  private gravity!: Gravity;
  private tmpTime: number = 0;

  public curveColumns: string[] = ['index', 'point', 'orientation', 'timestep', 'time', 'delete'];
  public dataTableSource!: MatTableDataSource<NozzlePoint>;
  public selection = new SelectionModel<any>(true, []);

  public point: NozzlePoint[] = [];

  public orientation = [
    { key: 'normal', value: 'Normal' },
    { key: 'gravity', value: 'Gravity' },
  ];
  public key = 'key';
  public value = 'value';
  public defaultOrientation = 'normal';
  private isNormal = true;

  public nozzleOffset: number = 0.1;
  public injectionTime: number = 0;

  public tableSize = 0;
  public clearInput: string = '';

  constructor(
    private pointSelectionService: PointSelectionService,
    private dialogRef: MatDialogRef<OpenMoldDialogComponent>,
    private snackbar: MatSnackBar
  ) {
    super();
  }

  ngOnInit(): void {
    this.injectionTime = this.data.injectionTime;
    this.modelId = this.data.modelId;
    this.gravity = this.data.gravity;

    this.pointSelectionService.getOrientation() === true
      ? (this.defaultOrientation = 'normal')
      : (this.defaultOrientation = 'gravity');

    this.assignInfo(this.pointSelectionService.getOrientation());

    this.tableSize = this.pointSelectionService.getInfoPoints().length - 1;
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes['saveTrigger'] && this.saveTrigger) {
      this.saveCalled.emit({ data: this.point });
    } else {
      this.resetTrigger.emit();
    }
  }

  private assignInfo(IsNormal?: boolean, reset?: boolean) {
    this.point = [];

    let time = 0;

    this.pointSelectionService.getInfoPoints().forEach((point, index) => {
      this.point.push(this.createData(point, IsNormal || false, reset || false, time, index));
    });

    this.dataTableSource = new MatTableDataSource<NozzlePoint>(this.point);
    this.dataTableSource.paginator = this.paginator;
  }

  private updateModel() {
    this.pointSelectionService.clearPoints(this.modelId);
    this.dataTableSource.data.forEach((point: NozzlePoint) => {
      if (point.strPosition && point.strOrientation) {
        const vec3Point = new Vec3(
          parseFloat(point.strPosition.split(',')[0]),
          parseFloat(point.strPosition.split(',')[1]),
          parseFloat(point.strPosition.split(',')[2])
        );
        const vec3Normal: Vec3 = new Vec3(
          parseFloat(point.strOrientation.split(',')[0]),
          parseFloat(point.strOrientation.split(',')[1]),
          parseFloat(point.strOrientation.split(',')[2])
        );
        this.pointSelectionService.updateInfoPoints(vec3Point, vec3Normal, this.modelId);
      }
    });
  }

  private createData(
    point: NozzlePoint,
    IsNormal: boolean,
    reset: boolean,
    time: number,
    index: number,
    isStr?: boolean
  ) {
    let strPoint: string = '';
    let strNormal: string = '';
    let normal: Vec3 = new Vec3(0, 0, 0);

    if (!isStr) {
      if (point.position && point.orientation) {
        strPoint =
          point.position.x.toFixed(6) + ', ' + point.position.y.toFixed(6) + ', ' + point.position.z.toFixed(6);
        strNormal =
          point.orientation.x.toFixed(6) +
          ', ' +
          point.orientation.y.toFixed(6) +
          ', ' +
          point.orientation.z.toFixed(6);
        //normal = point.orientation || new Vec3(0, 0, 0);
      }
    } else {
      strPoint = point.strPosition || '';
      strNormal = point.strOrientation || '';
      if (point.strPosition && point.strOrientation) {
        normal = new Vec3(
          parseFloat(point.strOrientation.split(',')[0]),
          parseFloat(point.strOrientation.split(',')[1]),
          parseFloat(point.strOrientation.split(',')[2])
        );
      }
    }

    if (!IsNormal) {
      const gravityVec3 = new Vec3(this.gravity.x, this.gravity.y, this.gravity.z);
      const nozzlePoint = new Vec3(point.position?.x || 0, point.position?.y || 0, point.position?.z || 0);
      let gravityVector = Vec3.sub(nozzlePoint, gravityVec3);
      gravityVector = Vec3.scale(gravityVector, 0.01);
      strNormal = gravityVector.x.toFixed(6) + ', ' + gravityVector.y.toFixed(6) + ', ' + gravityVector.z.toFixed(6);
    }

    if (IsNormal && reset) {
      const gravityVec3 = new Vec3(this.gravity.x, this.gravity.y, this.gravity.z);
      const normalVector = Vec3.add(normal, gravityVec3);
      strNormal = normalVector.x.toFixed(6) + ', ' + normalVector.y.toFixed(6) + ', ' + normalVector.z.toFixed(6);
    }

    const timestep = point.timestep || 0;

    time = this.tmpTime + timestep * this.injectionTime;
    this.tmpTime = time;

    const dict = {
      index: index + 1,
      strPosition: strPoint,
      strOrientation: strNormal,
      timestep: timestep * this.injectionTime,
      time: time,
    };

    return dict;
  }

  private updateTable(IsNormal?: boolean, reset?: boolean) {
    const tmpData: any[] = Helper.Array.deepCopy(this.dataTableSource.data);
    this.point = [];
    this.tmpTime = 0;
    this.dataTableSource.data = [];

    let time = 0;
    tmpData.forEach((point: NozzlePoint, index) => {
      this.point.push(this.createData(point, IsNormal || false, reset || false, time, index, true));
    });

    this.dataTableSource = new MatTableDataSource<any>(this.point);
    this.dataTableSource.paginator = this.paginator;
  }

  public changeOrientation(orientation: string) {
    if (orientation === 'gravity') {
      this.isNormal = false;
      this.updateTable(false);
    }

    if (orientation === 'normal') {
      this.isNormal = true;
      this.updateTable(true, true);
    }
  }

  // public updateTime(value: string, index: number, orientation: DropdownComponent)
  public updateTime(value: string, index: number) {
    this.dataTableSource.data[index].timestep = parseFloat(value) / this.injectionTime;

    for (let i = 1; i < this.dataTableSource.data.length - 2; i++) {
      if (i !== index) {
        const timeStep = this.dataTableSource.data[i].timestep || 0;
        const time = timeStep / this.injectionTime;

        this.dataTableSource.data[i].timestep = time;
      }
    }

    //const isNormal = ('orientation.default' === 'normal' ? true : false) as boolean;
    const isNormal = (true ? true : false) as boolean;
    this.updateTable(isNormal);
  }

  public deletePointInfo(index: number) {
    const realIndex = index + this.pageIndex * this.pageSize + 1;

    this.pointSelectionService.removeInfoPointAt(realIndex, 0);

    this.dataTableSource.data = [];

    this.assignInfo();
  }

  public onPaginateChange(event = { pageIndex: 0, pageSize: 0 }) {
    this.pageIndex = event['pageIndex'];
    this.pageSize = event['pageSize'];
  }

  public save() {
    this.updateModel();
    this.isNormal === true
      ? this.pointSelectionService.setOrientation(true)
      : this.pointSelectionService.setOrientation(false);
    this.dialogRef.close();
  }

  public copy() {
    let str;

    const data = this.dataTableSource.data;

    for (let i = 0; i < data.length; i++) {
      for (const [key, value] of Object.entries(data[i])) {
        if (key) {
          str = str !== undefined ? str + key + '\t' : key + '\t';
        }
      }
      break;
    }

    str = str + '\n';

    for (let i = 0; i < data.length; i++) {
      for (const [key, value] of Object.entries(data[i])) {
        if (value) {
          const valueStr = value as string;
          str = str + valueStr.toString().replace('.', ',') + '\t';
        }
      }

      str = str + '\n';
    }

    const selBox = document.createElement('textarea');
    selBox.style.position = 'fixed';
    selBox.style.left = '0';
    selBox.style.top = '0';
    selBox.style.opacity = '0';
    selBox.value = str;
    document.body.appendChild(selBox);
    selBox.focus();
    selBox.select();
    document.execCommand('copy');
    document.body.removeChild(selBox);

    this.snackbar.open('Added table to clipboard...', 'OK', { duration: 2000 });
  }

  public paste(event: ClipboardEvent) {
    let clipboardData = event.clipboardData;

    if (clipboardData) {
      let pastedText = clipboardData.getData('text');
      const splitted = pastedText.split(/\t?\n/);

      let data = [];

      for (var i = 1; i < splitted.length; i++) {
        if (splitted[i].split('\t')[0]) {
          const index = splitted[i].split('\t')[0];
          const strPosition = splitted[i].split('\t')[1];
          const strOrientation = splitted[i].split('\t')[2];
          const timestep = splitted[i].split('\t')[3];
          const time = splitted[i].split('\t')[4];

          const dict = {
            index: index,
            strPosition: strPosition,
            strOrientation: strOrientation,
            timestep: timestep,
            time: time,
          };
          data.push(dict);
        }
      }

      setTimeout(() => {
        this.clearInput = '';
      }, 1000);

      this.point = [];

      let time = 0;

      this.tableSize = data.length - 1;

      data.forEach((point) => {
        this.point.push({
          index: parseFloat(point.index),
          strPosition: point.strPosition,
          strOrientation: point.strOrientation,
          timestep: parseFloat(point.timestep) * this.injectionTime,
          time: time,
        });
      });

      this.dataTableSource = new MatTableDataSource<any>(this.point);
      this.dataTableSource.paginator = this.paginator;

      this.snackbar.open('Updated table...', 'OK', { duration: 2000 });
    }
  }
}
